import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { resetPasswordMutation } from '@/mutations/auth/resetPasswordMutation';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

const getFormSchema = (t: TFunction) =>
  z.object({
    email: z
      .string({
        required_error: t(
          'auth.resetPassword.form.fields.email.error.required'
        ),
      })
      .email({
        message: t('auth.resetPassword.form.fields.email.error.invalid'),
      }),
  });

export function ResetPasswordForm() {
  const { t } = useTranslation();

  const formSchema = getFormSchema(t);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  const { mutate: resetPassword, isPending } = useMutation({
    ...resetPasswordMutation(),
    onSuccess: () => {
      toast.success(t('auth.resetPassword.toast.success'));
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    resetPassword(values.email);
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        data-testid="reset-password-form"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('auth.resetPassword.form.fields.email.label')}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t(
                    'auth.resetPassword.form.fields.email.placeholder'
                  )}
                  {...field}
                  type="email"
                  data-testid="reset-password-email-input"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full"
          disabled={isPending}
          data-testid="reset-password-submit-button"
          isLoading={isPending}
        >
          {t('auth.resetPassword.send')}
        </Button>
      </form>
    </Form>
  );
}
